import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MegaMenuSlider from "src/components/ui-elements/MegaMenuSlider";
import {
  handleCategoryNavbarClick,
  handleProductCategoryNavbarClick,
} from "src/helpers/productHelpers";
import { useWindowDimensions } from "src/hooks/useWindowdimension";
import { fetchTrendingProducts } from "src/services/TrendingProductApi";
import { useSelector } from "react-redux";
import { isVideoPath } from "src/utils/imageUrl";

const GridLayout = ({ data, closeMenu }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [sliderPerView, setSlidesPerView] = useState(3);
  const { width } = useWindowDimensions();
  const navigate = useNavigate();

  const [animate, setAnimate] = useState(false);

  const { items: trendingProducts, loading } = useSelector(
    (state) => state.trendingProducts
  );

  useEffect(() => {
    if (trendingProducts.length === 0) {
      fetchTrendingProducts();
    }
  }, [trendingProducts.length]);

  useEffect(() => {
    if (width < 991) {
      setSlidesPerView(1);
    } else {
      setSlidesPerView(2);
    }
  }, [width]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => setAnimate(true), 350);
    return () => clearTimeout(timeout);
  }, []);

  const formatProductsForSlider = (products) => {
    return products.map(product => {
      // Cards can't play videos, so only consider photo entries
      const photos = (product.images || []).filter((img) => !isVideoPath(img?.imagePath));
      return {
        id: product.slug,
        name: product.name,
        price: product.price,
        oldPrice: product.markedPrice,
        image: photos.length > 0 ? photos[0].imagePath : "",
        hoverImage: photos.length > 1 ? photos[1].imagePath : (photos.length > 0 ? photos[0].imagePath : ""),
        type: product.category,
        tags: product.discount > 0 ? [{ type: "discount", value: `${product.discount}% OFF` }] : []
      };
    });
  };

  const getSliderWidth = () => {
    if (isMobile) {
      return { width: "100%" };
    }
    return { width: "45%" };
  };

  return (
    <div className="container mx-auto flex max-md:flex-col justify-between pt-6 pb-4">
      <div className={`grid md:grid-cols-3 gap-6 ${isMobile ? "w-full" : "w-[55%]"}`}>
        {/* Row 1 */}
        {data.row1.length > 0 && (
          <div
            className={`pl-2 transition-all duration-500 ease-out transform ${
              animate ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"
            }`}
            style={{ transitionDelay: "0ms" }}
          >
            <div className="md:space-y-2 space-y-1">
              {data.row1.map((item, subIndex) => (
                <div key={subIndex}>
                  {item.heading && (
                    <h4 className="font-outfitMedium text-[16x] md:text-[22px]">
                      <div
                        onClick={() => {
                          handleProductCategoryNavbarClick(
                            item.heading,
                            navigate
                          );
                          closeMenu();
                        }}
                        className="cursor-pointer hover:underline"
                        style={{ color: 'var(--navbar-link-color)' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--navbar-link-hover-color)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navbar-link-color)'}
                      >
                        {item.heading}
                      </div>
                    </h4>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Row 2 */}
        {data.row2.length > 0 && (
          <div
            className={`pl-5 border-l transition-all duration-500 ease-out transform ${
              animate ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"
            }`}
            style={{ transitionDelay: "100ms" }}
          >
            <div className="md:space-y-2 space-y-1">
              {data.row2.map((item, subIndex) => (
                <div key={subIndex}>
                  {item.heading && (
                    <h4 className="font-outfitMedium text-[16x] md:text-[22px]">
                      <div
                        className="cursor-pointer hover:underline text-[14px] font-normal font-outfitRegular"
                        style={{ color: 'var(--navbar-link-color)' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--navbar-link-hover-color)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navbar-link-color)'}
                        onClick={() => {
                          handleCategoryNavbarClick(item.heading, navigate);
                          closeMenu();
                        }}
                      >
                        {item.heading}
                      </div>
                    </h4>
                  )}
                  {item?.name && (
                    <div
                      className="cursor-pointer hover:underline text-[14px] font-normal font-outfitRegular"
                      style={{ color: 'var(--navbar-link-color)' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--navbar-link-hover-color)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navbar-link-color)'}
                      onClick={() => {
                        handleCategoryNavbarClick(item.name, navigate);
                        closeMenu();
                      }}
                    >
                      {item.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Row 3 */}
        {data.row3.length > 0 && (
          <div
            className={`pl-5 border-l transition-all duration-500 ease-out transform ${
              animate ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"
            }`}
            style={{ transitionDelay: "200ms" }}
          >
            <div className="md:space-y-2 space-y-1">
              {data.row3.map((item, subIndex) => (
                <div key={subIndex}>
                  {item.heading && (
                    <h4 className="font-outfitMedium text-[16x] md:text-[22px]">
                      <div
                        className="cursor-pointer hover:underline text-[14px] font-normal font-outfitRegular"
                        style={{ color: 'var(--navbar-link-color)' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--navbar-link-hover-color)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navbar-link-color)'}
                        onClick={() => {
                          handleCategoryNavbarClick(item.heading, navigate);
                          closeMenu();
                        }}
                      >
                        {item.heading}
                      </div>
                    </h4>
                  )}
                  {item?.name && (
                    <div
                      className="cursor-pointer hover:underline text-[14px] font-normal font-outfitRegular"
                      style={{ color: 'var(--navbar-link-color)' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--navbar-link-hover-color)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navbar-link-color)'}
                      onClick={() => {
                        handleCategoryNavbarClick(item.name, navigate);
                        closeMenu();
                      }}
                    >
                      {item.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right Slider */}
      <div
        className={`mt-4 md:mt-0 p-2 transition-all duration-500 ease-out transform ${
          animate ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"
        }`}
        style={{ ...getSliderWidth(), transitionDelay: "300ms" }}
      >
        <MegaMenuSlider
          sliderImages={loading ? [] : formatProductsForSlider(trendingProducts)}
          sliderPerView={sliderPerView}
          closeMenu={closeMenu}
        />
      </div>
    </div>
  );
};

export default GridLayout;
